import type { KeyboardEvent, MouseEvent } from 'react';
import { ThreadType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { GroupParticipant, GroupThreadContext } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { getDisplayName, DisplayNameControlKeys, useGetDisplayNameStore } from '@frontend/get-display-name';
import { AvatarProps } from '@frontend/design-system';

export { DisplayNameControlKeys, useGetDisplayNameStore };
export type { GroupThreadContext, GroupParticipant, ThreadType };

export const getGroupParticipantDisplayName = (participant: GroupParticipant): string => {
  const person = participant.person;
  const phoneNumber = participant.phoneNumber ?? '';

  if (!person) return phoneNumber;

  const { fullName } = getDisplayName({
    firstName: person.firstName?.trim() ?? '',
    lastName: person.lastName?.trim() ?? '',
    preferredName: person.preferredName?.trim(),
  });

  // Keep display fallbacks deterministic for easier debugging in UI and logs.
  return fullName.trim() || phoneNumber;
};

/**
 * Returns just the first name (or fallback) for a group participant.
 * Priority: firstName → preferredName → phoneNumber
 */
export const getGroupParticipantFirstName = (participant: GroupParticipant): string => {
  const person = participant.person;
  const phoneNumber = participant.phoneNumber ?? '';

  if (!person) return phoneNumber;

  // Priority: firstName → preferredName → phoneNumber
  return person.firstName?.trim() || person.preferredName?.trim() || phoneNumber;
};

/**
 * Determines whether a thread is a group MMS thread with multiple participants.
 * A thread is considered a group thread when:
 *  1. threadType is THREAD_TYPE_GROUP_MMS
 *  2. participantCount > 1 (at least 2 participants)
 */
export const isGroupThread = ({
  threadType,
  participantCount,
}: {
  threadType?: ThreadType;
  participantCount: number;
}): boolean => {
  return threadType === ThreadType.THREAD_TYPE_GROUP_MMS && participantCount > 1;
};

export const getGroupParticipantId = (participant: GroupParticipant): string => {
  const personId = participant.person?.personId ?? '';
  const phone = participant.phoneNumber ?? '';
  // Combine personId + phoneNumber when a person is known, ensuring two patients
  // with the same phone number produce distinct IDs. Fall back to phone-only for
  // anonymous participants (no personId).
  return personId ? `${personId}:${phone}` : phone;
};

export const groupParticipantsToAvatarProps = (
  participants: GroupParticipant[],
  onParticipantClick?: (participant: GroupParticipant, e?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void
): AvatarProps[] =>
  participants.map((participant) => {
    // AvatarProps is a design-system contract, so participant fields are mapped at this render boundary.
    const displayName = getGroupParticipantDisplayName(participant);

    return {
      id: getGroupParticipantId(participant),
      // profilePhoto is fetched via current person's Id which is set as userId here
      userId: participant.person?.personId,
      firstName: participant.person?.firstName,
      lastName: participant.person?.lastName,
      name: participant.person ? displayName : undefined,
      title: displayName,
      onClick: onParticipantClick ? (e) => onParticipantClick(participant, e) : undefined,
    };
  });
